/**
 * File intake: the visible drop target and the hidden file input behind its label.
 *
 * Both routes end at the same callback. Nothing here inspects the files — validation is
 * the domain layer's job, and this stays a transport.
 */

export interface UploadZoneOptions {
  readonly root: HTMLElement;
  readonly input: HTMLInputElement;
  readonly onFiles: (files: File[]) => void;
}

const DRAGGING_CLASS = 'upload--dragging';

export function createUploadZone({ root, input, onFiles }: UploadZoneOptions): void {
  input.addEventListener('change', () => {
    if (input.files) onFiles([...input.files]);
    // Clearing it means re-adding the same file straight after a failure still fires a
    // change event, rather than looking like nothing happened.
    input.value = '';
  });

  root.addEventListener('submit', (event) => {
    event.preventDefault();
  });

  let dragDepth = 0;

  root.addEventListener('dragenter', (event) => {
    event.preventDefault();
    dragDepth += 1;
    root.classList.add(DRAGGING_CLASS);
  });

  root.addEventListener('dragover', (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  });

  // Dragging over a child element fires leave on the parent, so a simple toggle would
  // flicker. Counting enters against leaves does not.
  root.addEventListener('dragleave', () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) root.classList.remove(DRAGGING_CLASS);
  });

  root.addEventListener('drop', (event) => {
    event.preventDefault();
    dragDepth = 0;
    root.classList.remove(DRAGGING_CLASS);

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) onFiles([...files]);
  });

  // Without this, dropping a file anywhere else on the page makes the browser navigate
  // away from the app to display it — a uniquely annoying way to lose your session.
  for (const type of ['dragover', 'drop'] as const) {
    window.addEventListener(type, (event) => {
      if (!root.contains(event.target as Node)) event.preventDefault();
    });
  }
}
